import { getAllDoctors, getDoctorById } from "../services/doctorService";
import { getAllStaff, getStaffDetailByUserId } from "../services/StaffService";

const DUPLICATE_MESSAGES = {
  mobile: "Mobile number already exists in this hospital organization",
  email: "Email already exists in this hospital organization",
  aadhar: "Aadhar number already exists in this hospital organization",
  registrationNumber: "Registration Number already exists in this hospital organization",
};

let __enrichedCache = { data: null, ts: 0, doctorCount: -1, staffCount: -1 };
const ENRICHED_TTL = 60000;

/** API / legacy field names per logical field */
const FIELD_ALIASES = {
  aadhar: ["aadhar", "aadhaar", "aadharNumber", "aadhaarNumber"],
  mobile: ["mobile", "mobileNumber", "contactNumber", "phone"],
  email: ["email", "emailAddress"],
  registrationNumber: ["registrationNumber", "registration_number", "regNumber"],
};

const ORG_TEAM_UNIQUE_FIELDS = ["mobile", "email", "aadhar"];

const normalizeValue = (field, value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  if (field === "email") return trimmed.toLowerCase();
  if (field === "aadhar" || field === "mobile") {
    return trimmed.replace(/\D/g, "");
  }
  return trimmed;
};

const getRecordValue = (record, field) => {
  if (!record) return "";
  const keys = FIELD_ALIASES[field] || [field];
  for (const key of keys) {
    if (record[key] != null && String(record[key]).trim() !== "") {
      return normalizeValue(field, record[key]);
    }
  }
  return "";
};

const getMemberUserId = (member) => member?.userId ?? member?.user_id ?? null;

/**
 * Normalize exclude context for edit flows.
 * Accepts legacy userId string/number or { userId, doctorId, staffId }.
 */
export const normalizeExcludeContext = (exclude) => {
  if (exclude == null || exclude === "") return null;
  if (typeof exclude === "object") {
    return {
      userId: exclude.userId ?? exclude.user_id ?? null,
      doctorId: exclude.doctorId ?? null,
      staffId: exclude.staffId ?? null,
    };
  }
  return { userId: exclude, doctorId: null, staffId: null };
};

/** Exclude context when editing a doctor record */
export const buildDoctorExcludeContext = (doctor) => {
  if (!doctor) return null;
  const userId = getMemberUserId(doctor);
  const doctorId = doctor.id ?? doctor.idDoctor ?? null;
  if (userId == null && doctorId == null) return null;
  return { userId, doctorId };
};

/** Exclude context when editing a staff record */
export const buildStaffExcludeContext = (staff) => {
  if (!staff) return null;
  const userId = getMemberUserId(staff);
  const staffId = staff.id ?? null;
  if (userId == null && staffId == null) return null;
  return { userId, staffId };
};

const shouldSkipMember = (member, excludeContext) => {
  if (!excludeContext || !member) return false;

  const memberUserId = getMemberUserId(member);
  if (
    excludeContext.userId != null &&
    memberUserId != null &&
    String(memberUserId) === String(excludeContext.userId)
  ) {
    return true;
  }

  if (
    excludeContext.doctorId != null &&
    member.__teamKind === "doctor" &&
    member.id != null &&
    String(member.id) === String(excludeContext.doctorId)
  ) {
    return true;
  }

  if (
    excludeContext.staffId != null &&
    member.__teamKind === "staff" &&
    member.id != null &&
    String(member.id) === String(excludeContext.staffId)
  ) {
    return true;
  }

  return false;
};

/**
 * Load all doctors and staff for the current organization.
 * List endpoints don't return all fields (doctors miss aadhar/mobile/email;
 * staff misses registrationNumber), so we enrich each record with a detail
 * fetch. A short-lived cache prevents redundant calls during a submit flow.
 */
export const getOrgTeamMembers = async () => {
  const [doctors, staff] = await Promise.all([
    getAllDoctors().catch(() => []),
    getAllStaff().catch(() => []),
  ]);
  const doctorList = Array.isArray(doctors) ? doctors : [];
  const staffList = Array.isArray(staff) ? staff : [];

  const now = Date.now();
  const cacheValid =
    __enrichedCache.data &&
    now - __enrichedCache.ts < ENRICHED_TTL &&
    __enrichedCache.doctorCount === doctorList.length &&
    __enrichedCache.staffCount === staffList.length;

  if (cacheValid) {
    return __enrichedCache.data;
  }

  const [enrichedDoctors, enrichedStaff] = await Promise.all([
    Promise.all(
      doctorList.map(async (doc) => {
        if (!doc.id) return { ...doc, __teamKind: "doctor" };
        try {
          const full = await getDoctorById(doc.id);
          return { ...doc, ...full, __teamKind: "doctor" };
        } catch (e) {
          return { ...doc, __teamKind: "doctor" };
        }
      })
    ),
    Promise.all(
      staffList.map(async (s) => {
        const staffUserId = getMemberUserId(s);
        if (!staffUserId) return { ...s, __teamKind: "staff" };
        try {
          const full = await getStaffDetailByUserId(staffUserId);
          return { ...s, ...full, __teamKind: "staff" };
        } catch (e) {
          return { ...s, __teamKind: "staff" };
        }
      })
    ),
  ]);

  const result = [...enrichedDoctors, ...enrichedStaff];

  if (enrichedStaff.length > 0) {
    const sample = enrichedStaff[0];
    console.log("[orgUserUniqueness] Staff enrichment sample fields:", Object.keys(sample));
    console.log("[orgUserUniqueness] Staff sample registrationNumber:", sample.registrationNumber ?? sample.registration_number ?? "(missing)");
  }

  __enrichedCache = {
    data: result,
    ts: Date.now(),
    doctorCount: doctorList.length,
    staffCount: staffList.length,
  };
  return result;
};

/**
 * Returns true if no other team member in the org has the same field value.
 * @param {string} field - mobile | email | aadhar
 * @param {string} value
 * @param {number|string|object|null} exclude - userId or { userId, doctorId, staffId } to exclude on edit
 */
export const isOrgTeamFieldUnique = async (field, value, exclude = null) => {
  const normalizedInput = normalizeValue(field, value);
  if (!normalizedInput) return true;

  try {
    const members = await getOrgTeamMembers();
    const excludeContext = normalizeExcludeContext(exclude);

    return !members.some((member) => {
      if (shouldSkipMember(member, excludeContext)) return false;
      return getRecordValue(member, field) === normalizedInput;
    });
  } catch (error) {
    console.error("[orgUserUniqueness] Uniqueness check failed:", error);
    return true;
  }
};

/**
 * Check a single field's uniqueness against pre-fetched members (no extra API call).
 */
const checkFieldUniqueAgainstMembers = (members, field, value, excludeContext) => {
  const normalizedInput = normalizeValue(field, value);
  if (!normalizedInput) return true;

  if (field === "registrationNumber") {
    members.forEach((m, i) => {
      const v = getRecordValue(m, field);
      const skipped = shouldSkipMember(m, excludeContext);
      if (v) {
        console.log(
          `[orgUserUniqueness] member[${i}] regNum="${v}" userId=${getMemberUserId(m)} skipped=${skipped} match=${v === normalizedInput}`
        );
      }
    });
  }

  return !members.some((member) => {
    if (shouldSkipMember(member, excludeContext)) return false;
    return getRecordValue(member, field) === normalizedInput;
  });
};

/**
 * Validate unique fields against all doctors and staff in the org.
 *
 * `fields` accepts two formats:
 *   - Flat array   — ["mobile", "email", "aadhar"]  (all checked together)
 *   - Priority groups — [["aadhar","mobile"], ["email","registrationNumber"]]
 *     Groups are checked in order; if any field in a group is a duplicate the
 *     errors are returned immediately and later groups are skipped.
 *
 * @param {number|string|object|null} exclude - userId or { userId, doctorId, staffId } to exclude on edit
 * @returns {Promise<Record<string, string>>} Formik-style field errors
 */
export const validateOrgTeamUserFields = async (
  values,
  exclude = null,
  fields = ORG_TEAM_UNIQUE_FIELDS
) => {
  const isPriorityGroups = Array.isArray(fields[0]);
  const fieldGroups = isPriorityGroups ? fields : [fields];

  let members;
  try {
    members = await getOrgTeamMembers();
  } catch (error) {
    console.error("[orgUserUniqueness] Failed to fetch team members:", error);
    return {};
  }

  if (!members || members.length === 0) {
    console.warn("[orgUserUniqueness] No team members returned — skipping uniqueness check");
    return {};
  }

  const excludeContext = normalizeExcludeContext(exclude);

  const regCount = members.filter(
    (m) => getRecordValue(m, "registrationNumber") !== ""
  ).length;
  console.log(
    `[orgUserUniqueness] Checking ${members.length} team members (exclude=${JSON.stringify(excludeContext)}, membersWithRegNum=${regCount})`
  );

  for (const group of fieldGroups) {
    const errors = {};
    for (const field of group) {
      const raw = values[field];
      if (raw == null || String(raw).trim() === "") {
        console.log(`[orgUserUniqueness] Skip "${field}": empty/null`);
        continue;
      }
      const isUnique = checkFieldUniqueAgainstMembers(members, field, raw, excludeContext);
      console.log(
        `[orgUserUniqueness] Check "${field}": formValue="${normalizeValue(field, raw)}", unique=${isUnique}`
      );
      if (!isUnique) {
        errors[field] =
          DUPLICATE_MESSAGES[field] ||
          "Value already exists in this hospital organization";
      }
    }
    if (Object.keys(errors).length > 0) {
      console.log("[orgUserUniqueness] Returning errors from group:", Object.keys(errors));
      return errors;
    }
  }

  return {};
};

/** Mark fields with validation errors as touched so messages appear in the UI */
export const touchFormikErrorFields = (formik, errors) => {
  if (!errors || !formik?.setFieldTouched) return;
  Object.keys(errors).forEach((key) => {
    formik.setFieldTouched(key, true, false);
  });
};

export default {
  getOrgTeamMembers,
  isOrgTeamFieldUnique,
  validateOrgTeamUserFields,
  touchFormikErrorFields,
  normalizeExcludeContext,
  buildDoctorExcludeContext,
  buildStaffExcludeContext,
  ORG_TEAM_UNIQUE_FIELDS,
};
